// Prefix for per-user page-size preference (durable localStorage)
const PREFIX = "partners:page-size";

// Ignore (layout) lists to skip when walking parents for an owner name
const SKIP_OWNER_NAMES = new Set([
	"paginate",
	"Paginate",
	"RouterView",
	"RouterLink",
	"transition",
	"Transition",
	"KeepAlive",
	"BaseTransition",
	"Layout",
	"layout",
]);

// Build storage key: `partners:page-size:${userId}:${listKey}`
export function pageSizeStorageKey(userId, listKey) {
	return `${PREFIX}:${userId}:${listKey || "default"}`;
}

// Embeds/modals get `${route}:${OwnerName}`; normal page lists keep `${route}` only.
function isEmbedOwnerName(name) {
	return /Modal$|Widget$|Table$|Panel$/i.test(String(name || ""));
}

/**
 * @param {{ routeName?: string|null, ownerName?: string|null }} p
 */
export function resolvePageSizeStorageKey(p = {}) {
	const route = p.routeName ? String(p.routeName) : "default";
	if (p.ownerName && isEmbedOwnerName(p.ownerName)) {
		return `${route}:${p.ownerName}`;
	}
	return route;
}

// Walk Vue parents for the nearest named owner (skip layout/router chrome)
export function findPageSizeOwnerName(vm) {
	let p = vm && vm.$parent;
	while (p) {
		const name = p.$options && p.$options.name;
		if (name && !SKIP_OWNER_NAMES.has(name)) {
			return String(name);
		}
		p = p.$parent;
	}
	return null;
}

/**
 * Read stored page size; return fallback if missing/invalid/not in options.
 * @param {string|number} userId
 * @param {string} listKey
 * @param {number[]} options
 * @param {number} fallback
 */
export function getStoredPageSize(userId, listKey, options, fallback) {

	// No user ID - return fallback
	if (userId == null || userId === "") {
		return fallback;
	}

	// Get stored page size
	try {
		const raw = localStorage.getItem(pageSizeStorageKey(userId, listKey));
		if (raw == null) {
			return fallback;
		}

		// Return stored page size
		const size = Number(raw);
		if (!Number.isFinite(size) || !options.includes(size)) {
			return fallback;
		}
		return size;
	} catch (e) {
		return fallback;
	}
}

/**
 * Persist page size for user + list.
 * @param {string|number} userId
 * @param {string} listKey
 * @param {number} size
 */
export function setStoredPageSize(userId, listKey, size) {

	// No user ID - return false
	if (userId == null || userId === "") {
		return false;
	}

	// Set stored page size
	try {
		localStorage.setItem(pageSizeStorageKey(userId, listKey), String(size));
		return true;
	} catch (e) {
		return false;
	}
}
